import React, { forwardRef } from "react";
import { useDebouncedCallback } from "use-debounce";

import { TextAreaProps } from "./types";
import { classNames } from "helpers";
import { ReactSVG } from "react-svg";
import { Tooltip } from "ui/components/Tooltip";

const TYPING_DEBOUNCE_TIME = 500;

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      id,
      value,
      onChange,
      rows,
      name,
      label,
      placeholder,
      readOnly,
      disableDebounce = false,
      disabled,
      disableAutoFocus = false,
      required,
      error,
      startIcon,
      endIcon,
      size,
      cssClassName,
      ...otherProps
    },
    ref
  ) => {
    const canEdit = true;

    const isDisabled = disabled || !canEdit;
    const isError = !!error && !value;

    React.useEffect(() => {
      setLastEvent({
        target: { value: value === null ? "" : value },
      } as any);
    }, [value]);

    const [lastEvent, setLastEvent] = React.useState<
      React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    >({
      target: { value: value === null ? "" : value },
    } as any);

    const denouncedOnChange = useDebouncedCallback(() => {
      if (onChange) {
        onChange(lastEvent);
      }
    }, TYPING_DEBOUNCE_TIME);

    const handleOnChanged = (
      e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
      e.persist();
      setLastEvent(e);
      denouncedOnChange();
    };

    const handleOnBlur = () => {
      denouncedOnChange.flush();
    };

    const handleOnFocus = (
      e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      e.target.select();
    };

    const sizeClassName = () => {
      switch (size) {
        case "min":
          return "w-24";
        case "small":
          return "w-48";
        case "medium":
          return "w-96";
        case "large":
          return "w-full";
        case "full":
          return "w-full";
        default:
          return "w-full";
      }
    };

    const requiredClassName = () => {
      if (isError && required) {
        // `focus:!ring-red-500` jer bi inače focus ring iz commonClassName pregazio crveni obrub.
        return "bg-red-50 ring-1 ring-red-500 text-red-900 placeholder-red-700 focus:!ring-red-500";
      } else {
        return "ring-1 ring-slate-300 bg-slate-50 ";
      }
    };

    const commonClassName = () => {
      return "relative cursor-default rounded-sm py-2.5 pl-3 pr-3 text-left text-gray-800 placeholder:text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-400 sm:text-sm sm:leading-6 z-0 disabled:cursor-default disabled:bg-gray-100 disabled:text-gray-900 disabled:border-gray-300 disabled:shadow-none";
    };

    return (
      <>
        {label && (
          <label className="block mb-2 text-sm font-medium text-gray-700">
            {label}
          </label>
        )}

        <div className="flex rounded-sm relative">
          {startIcon && (
            <span className="absolute top-2/4 -translate-y-1/2 left-2 z-20 block">
              <ReactSVG src={startIcon} className="w-4 h-4 stroke-[#667085]" />
            </span>
          )}

          <textarea
            ref={ref}
            id={id}
            rows={rows ?? 3}
            value={
              disableDebounce
                ? value === null
                  ? ""
                  : value
                : lastEvent.target.value
            }
            name={name}
            disabled={isDisabled}
            onChange={disableDebounce ? onChange : handleOnChanged}
            onBlur={disableDebounce ? undefined : handleOnBlur}
            onFocus={disableAutoFocus ? undefined : handleOnFocus}
            className={classNames(
              startIcon && "pl-8",
              endIcon && "pr-8",
              commonClassName(),
              sizeClassName(),
              requiredClassName(),
              cssClassName
            )}
            placeholder={placeholder}
            required={required}
            readOnly={readOnly}
            {...otherProps}
          />

          {endIcon && (
            <span className="absolute top-2/4 -translate-y-1/2 right-2 z-20 block">
              <ReactSVG src={endIcon} className="w-4 h-4 stroke-[#888a97]" />
            </span>
          )}

          {isError && (
            <div className="absolute right-2 top-2/4 -translate-y-1/2">
              <Tooltip description={error} />
            </div>
          )}
        </div>
      </>
    );
  }
);

TextArea.displayName = "TextArea";

export default React.memo(TextArea);
